/**
 * Notifications API Service
 *
 * Handles notification settings (configs) and notifications inbox.
 * Based on spec: /api/v1/notification-settings and /api/v1/notifications
 */

import api from "../client";
import { ENDPOINTS } from "../endpoints";
import type {
  NotificationConfig,
  NotificationConfigCreate,
  NotificationConfigUpdate,
  NotificationSettingsDiscoveryResponse,
  InAppNotification,
  InAppNotificationDetail,
  InAppNotificationParams,
  UnreadCountResponse,
  MarkAsReadResponse,
} from "../types/notifications";

export const notificationsApi = {
  // ============================================================================
  // Notification Settings (Config CRUD) - /api/v1/notification-settings
  // ============================================================================

  /**
   * Discover available options for notification configuration
   * GET /api/v1/notification-settings/discovery
   * Returns storm_impact_models (with weather_models and variables), geographic_hierarchies, and comparison_operators
   */
  getDiscoveryOptions:
    async (): Promise<NotificationSettingsDiscoveryResponse> => {
      return api.get<NotificationSettingsDiscoveryResponse>(
        ENDPOINTS.NOTIFICATION_SETTINGS_DISCOVERY
      );
    },

  /**
   * List user's notification configs
   * GET /api/v1/notification-settings
   * Note: API returns array directly, not wrapped in { items: [...] }
   */
  listConfigs: async (params?: {
    geographic_node_id?: string;
    is_active?: boolean;
  }): Promise<NotificationConfig[]> => {
    return api.get<NotificationConfig[]>(ENDPOINTS.NOTIFICATION_SETTINGS, {
      params,
    });
  },

  /**
   * Get a specific notification config
   * GET /api/v1/notification-settings/{id}
   */
  getConfig: async (id: string): Promise<NotificationConfig> => {
    return api.get<NotificationConfig>(ENDPOINTS.NOTIFICATION_SETTING(id));
  },

  /**
   * Create a new notification config
   * POST /api/v1/notification-settings
   */
  createConfig: async (
    data: NotificationConfigCreate
  ): Promise<NotificationConfig> => {
    return api.post<NotificationConfig>(ENDPOINTS.NOTIFICATION_SETTINGS, data);
  },

  /**
   * Update an existing notification config
   * PATCH /api/v1/notification-settings/{id}
   */
  updateConfig: async (
    id: string,
    data: NotificationConfigUpdate
  ): Promise<NotificationConfig> => {
    return api.patch<NotificationConfig>(
      ENDPOINTS.NOTIFICATION_SETTING(id),
      data
    );
  },

  /**
   * Delete (archive) a notification config
   * DELETE /api/v1/notification-settings/{id}
   * Note: Returns 204 No Content on success
   */
  deleteConfig: async (id: string): Promise<void> => {
    await api.delete(ENDPOINTS.NOTIFICATION_SETTING(id));
  },

  // ============================================================================
  // Notifications Inbox - /api/v1/notifications
  // ============================================================================

  /**
   * Get unread notification count for badge
   * GET /api/v1/notifications/stats
   */
  getUnreadCount: async (): Promise<UnreadCountResponse> => {
    return api.get<UnreadCountResponse>(ENDPOINTS.NOTIFICATIONS_STATS);
  },

  /**
   * List daily notification summaries
   * GET /api/v1/notifications
   * Note: API returns array directly
   */
  listNotifications: async (
    params?: InAppNotificationParams
  ): Promise<InAppNotification[]> => {
    return api.get<InAppNotification[]>(ENDPOINTS.NOTIFICATIONS, {
      params,
    });
  },

  /**
   * Get full notification detail for a date (includes HTML content)
   * GET /api/v1/notifications/{date}
   */
  getNotificationDetail: async (
    date: string
  ): Promise<InAppNotificationDetail> => {
    return api.get<InAppNotificationDetail>(
      ENDPOINTS.NOTIFICATION_BY_DATE(date)
    );
  },

  /**
   * Mark notifications for a date as read
   * PATCH /api/v1/notifications/{date}
   */
  markAsRead: async (date: string): Promise<MarkAsReadResponse> => {
    return api.patch<MarkAsReadResponse>(
      ENDPOINTS.NOTIFICATION_BY_DATE(date),
      {}
    );
  },

  /**
   * Mark ALL notifications as read
   * POST /api/v1/notifications/read-all
   */
  markAllAsRead: async (): Promise<MarkAsReadResponse> => {
    return api.post<MarkAsReadResponse>(ENDPOINTS.NOTIFICATIONS_READ_ALL);
  },
};

export default notificationsApi;
